/**
 * Bộ nhớ đệm bài giảng (.zip) trong IndexedDB — chỉ loại bài giảng, mã hóa AES-GCM.
 * Khóa AES lấy từ backend (sinh từ ELEARNING_LESSON_CACHE_MASTER_KEY trong .env BE), lưu sessionStorage.
 * Đóng tab / đăng xuất → xóa khóa phiên; dữ liệu ciphertext có thể còn trong IndexedDB.
 */

import { getLessonCacheCryptoKey } from "../api/elearning/documents/documentAccess";

const DB_NAME = "ima_edu_secure_lesson_docs";
const STORE = "docs";
const VERSION = 1;
const MAX_BAIGIANG_ENTRIES = 5;
const SESSION_KEY_B64 = "ima_lesson_cache_aes_key_b64";

function isBaiGiangType(t) {
  return t === "baigiang" || t === "bai_giang";
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "documentId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function base64ToUint8(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function importRawAesKeyFromBase64(b64) {
  const raw = base64ToUint8(b64);
  if (raw.byteLength !== 32) throw new Error("Khóa cache không hợp lệ.");
  return crypto.subtle.importKey("raw", raw, { name: "AES-GCM", length: 256 }, false, [
    "encrypt",
    "decrypt",
  ]);
}

/** Khóa phiên (sao chép từ BE khi đã đăng nhập). */
async function getOrCreateAesKey() {
  if (typeof crypto === "undefined" || !crypto.subtle) {
    throw new Error("Trình duyệt không hỗ trợ mã hóa (Web Crypto).");
  }
  let b64 = null;
  try {
    b64 = sessionStorage.getItem(SESSION_KEY_B64);
  } catch {
    /* private mode */
  }
  if (b64) {
    return importRawAesKeyFromBase64(b64);
  }
  const fromServer = await getLessonCacheCryptoKey();
  if (!fromServer || typeof fromServer !== "string") {
    throw new Error("Không lấy được khóa giải mã từ máy chủ.");
  }
  try {
    sessionStorage.setItem(SESSION_KEY_B64, fromServer);
  } catch {
    /* không lưu được sessionStorage */
  }
  return importRawAesKeyFromBase64(fromServer);
}

/**
 * Xóa khóa phiên (gọi khi đăng xuất). Dữ liệu IndexedDB vẫn là ciphertext.
 */
export function clearLessonCacheSessionKey() {
  try {
    sessionStorage.removeItem(SESSION_KEY_B64);
  } catch {
    /* ignore */
  }
}

/** Có khóa phiên từ BE trong sessionStorage — không có thì không giải mã được cache IndexedDB. */
export function hasLessonCacheSessionKey() {
  try {
    return Boolean(sessionStorage.getItem(SESSION_KEY_B64));
  } catch {
    return false;
  }
}

async function encryptBuffer(key, plain) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plain);
  return { iv, ciphertext };
}

async function decryptBuffer(key, iv, ciphertext) {
  return crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext);
}

async function getAllRows() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const r = tx.objectStore(STORE).getAll();
    r.onsuccess = () => resolve(r.result || []);
    r.onerror = () => reject(r.error);
  });
}

async function deleteById(documentId) {
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(documentId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function evictOldestBaiGiangIfNeeded() {
  const rows = await getAllRows();
  if (rows.length <= MAX_BAIGIANG_ENTRIES) return;
  const sorted = [...rows].sort((a, b) => (a.savedAt ?? 0) - (b.savedAt ?? 0));
  const excess = sorted.length - MAX_BAIGIANG_ENTRIES;
  for (let i = 0; i < excess; i++) {
    await deleteById(sorted[i].documentId);
  }
}

/**
 * @param {object} doc
 * @param {ArrayBuffer} plainBuffer
 * @param {string} revision
 */
export async function putEncryptedLessonDocument(doc, plainBuffer, revision) {
  if (!doc?.id || !plainBuffer || typeof indexedDB === "undefined") return;
  if (!isBaiGiangType(doc.type)) return;
  const key = await getOrCreateAesKey();
  const { iv, ciphertext } = await encryptBuffer(key, plainBuffer);
  const row = {
    documentId: doc.id,
    category: "baigiang",
    revision: String(revision ?? ""),
    iv: Array.from(iv),
    ciphertext,
    savedAt: Date.now(),
  };
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(row);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  await evictOldestBaiGiangIfNeeded();
}

/**
 * @param {string} documentId
 * @param {string} revision
 * @returns {Promise<ArrayBuffer | null>}
 */
/** Chỉ đọc metadata IndexedDB — không giải mã (dùng để biết đã tải bài giảng hay chưa). */
export async function hasSecureBaiGiangCache(documentId, revision) {
  if (!documentId || typeof indexedDB === "undefined") return false;
  let row = null;
  try {
    const db = await openDb();
    row = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const r = tx.objectStore(STORE).get(documentId);
      r.onsuccess = () => resolve(r.result ?? null);
      r.onerror = () => reject(r.error);
    });
  } catch {
    return false;
  }
  if (!row) return false;
  return String(row.revision ?? "") === String(revision ?? "");
}

export async function getDecryptedLessonDocumentIfMatch(documentId, revision) {
  if (!documentId || typeof indexedDB === "undefined") return null;
  let row = null;
  try {
    const db = await openDb();
    row = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const r = tx.objectStore(STORE).get(documentId);
      r.onsuccess = () => resolve(r.result ?? null);
      r.onerror = () => reject(r.error);
    });
  } catch {
    return null;
  }
  if (!row || String(row.revision ?? "") !== String(revision ?? "")) return null;
  try {
    const key = await getOrCreateAesKey();
    const iv = new Uint8Array(row.iv);
    return await decryptBuffer(key, iv, row.ciphertext);
  } catch {
    return null;
  }
}

export { MAX_BAIGIANG_ENTRIES as MAX_PER_CATEGORY };
